import { z } from "zod";
import type {
  FacilityListItem,
  FactorKey,
  HubFacts,
  HubHealthThresholds,
  HubSeriesKey,
} from "./api";

/**
 * Pure display helpers for Hub Health. Nothing here computes a score or writes
 * explanatory text — the backend owns both, so the page can never disagree
 * with the number that produced a score. This only formats and picks.
 */

export const MISSING = "n/a";

/** The same shape the backend's `params.guid` accepts. */
const GUID_RE =
  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

/**
 * The page's URL state: which facility is open. A value that isn't a GUID is
 * dropped rather than sent, since the backend would only reject it.
 *
 * The snapshot date is deliberately not in the URL: the detail always reads the
 * date the list returned, so a shared link opens on the current snapshot.
 */
export const hubHealthSearchSchema = z.object({
  facility: z.string().trim().regex(GUID_RE).optional().catch(undefined),
});

export type ScoreTone = "positive" | "warning" | "negative" | "neutral";

/**
 * The two marks on every score bar, and the cut points of its three colours:
 * red under 40, amber to 70, green from 70. These match the backend's `band()`
 * boundaries for "poor" and "good" (hub_health.py), so the colour never
 * contradicts the band word shown beside it. Change both together.
 */
export const SCORE_MARKS = [40, 70] as const;

/**
 * The colour for a 0–100 score. Used everywhere a score is drawn — including
 * facility factors, which carry no band from the API — so one number always
 * gets one colour. The band word, where there is one, is still the API's.
 */
export function scoreTone(score: number | null | undefined): ScoreTone {
  if (score === null || score === undefined) {
    return "neutral";
  }
  const [low, high] = SCORE_MARKS;
  if (score >= high) {
    return "positive";
  }
  if (score >= low) {
    return "warning";
  }
  return "negative";
}

/** First block of an id plus the next, for a narrow list; the full id goes in a title. */
export function shortId(id: string): string {
  return id.length > 13 ? `${id.slice(0, 13)}…` : id;
}

/** A 0–100 score, or "n/a" — null means not measured, never zero. */
export function formatScore(score: number | null | undefined): string {
  if (score === null || score === undefined) {
    return MISSING;
  }
  return score.toLocaleString(undefined, { maximumFractionDigits: 1 });
}

/**
 * A count. The payload sends decimals ("33.0", and genuinely fractional
 * `missed`), so this rounds for display only.
 */
export function formatCount(value: number | null | undefined): string {
  if (value === null || value === undefined) {
    return MISSING;
  }
  return Math.round(value).toLocaleString();
}

/** A measurement with a unit, to a fixed number of decimals. */
export function formatMeasure(
  value: number | null | undefined,
  unit: string,
  digits = 1,
): string {
  if (value === null || value === undefined) {
    return MISSING;
  }
  const number = value.toLocaleString(undefined, {
    maximumFractionDigits: digits,
    minimumFractionDigits: digits,
  });
  return unit ? `${number} ${unit}` : number;
}

/** A 0–1 fraction as a percent. */
export function formatFraction(value: number | null | undefined): string {
  if (value === null || value === undefined) {
    return MISSING;
  }
  return `${(value * 100).toLocaleString(undefined, { maximumFractionDigits: 1 })}%`;
}

/** Facility ids containing `term`, case-insensitively. Order is preserved. */
export function filterFacilities(
  facilities: FacilityListItem[],
  term: string,
): FacilityListItem[] {
  const needle = term.trim().toLowerCase();
  if (!needle) {
    return facilities;
  }
  return facilities.filter((facility) =>
    facility.id.toLowerCase().includes(needle),
  );
}

type FactorChart = {
  series: HubSeriesKey;
  label: string;
  /** Unit of the series values, for the axis title. */
  unit: (delayUnit: string) => string;
  /** Scale markers drawn as reference lines, in the series' own units. */
  markers: (
    thresholds: HubHealthThresholds,
  ) => { label: string; value: number }[];
};

/**
 * Which series each factor charts, per the contract. Redundancy has none: it is
 * a single number. There is no RSSI series and there can't be one, so placement
 * charts transmission time.
 *
 * Markers are only drawn where a threshold is in the series' units. Delivery's
 * and schedule's thresholds are percents of a total, not counts, so a line at
 * "25" on a chart of missed readings would be wrong.
 */
export const FACTOR_CHARTS: Record<FactorKey, FactorChart | null> = {
  timeliness: {
    series: "delay",
    label: "Average delay",
    unit: (delayUnit) => delayUnit,
    markers: (t) => [
      { label: "Full marks", value: t.delay_good },
      { label: "Zero", value: t.delay_zero },
    ],
  },
  utilisation: {
    series: "scheduled",
    label: "Scheduled readings",
    unit: () => "readings / day",
    markers: (t) => [
      { label: "Comfortable", value: t.util_good },
      { label: "Ceiling", value: t.capacity },
    ],
  },
  delivery: {
    series: "missed",
    label: "Missed readings",
    unit: () => "readings",
    markers: () => [],
  },
  redundancy: null,
  placement: {
    series: "tx",
    label: "Time on air",
    unit: () => "seconds",
    markers: (t) => [
      { label: "Full marks", value: t.tx_good },
      { label: "Zero", value: t.tx_zero },
    ],
  },
  schedule: {
    series: "readings",
    label: "Readings",
    unit: () => "readings",
    markers: () => [],
  },
};

export function factorChart(key: string): FactorChart | null {
  return (FACTOR_CHARTS as Record<string, FactorChart | null>)[key] ?? null;
}

/**
 * One point per day label. A null stays null, which the chart draws as a gap —
 * so `missed` ends a day early instead of dropping to zero.
 */
export function seriesPoints(
  dayLabels: string[],
  values: (number | null)[] | null,
): { x: number; y: number | null }[] {
  return dayLabels.map((_, index) => ({
    x: index,
    y: values?.[index] ?? null,
  }));
}

/** True when a series has at least one real point to draw. */
export function hasData(values: (number | null)[] | null): boolean {
  return Boolean(values?.some((value) => value !== null));
}

type FactRow = { label: string; value: string };

/** The facts that explain one factor, formatted. Rows come only from `facts`. */
export function factorFacts(
  key: string,
  facts: HubFacts,
  delayUnit: string,
): FactRow[] {
  switch (key) {
    case "timeliness":
      return [
        {
          label: "Average delay",
          value: formatMeasure(facts.avg_delay, delayUnit),
        },
        { label: "Readings", value: formatCount(facts.readings) },
      ];
    case "utilisation":
      return [
        {
          label: "Scheduled per day",
          value: formatCount(facts.scheduled_per_day),
        },
        {
          label: "Share of capacity",
          value: formatFraction(facts.capacity_share),
        },
        { label: "Sensors in range", value: formatCount(facts.in_range) },
      ];
    case "delivery":
      return [
        { label: "Expected", value: formatCount(facts.expected) },
        { label: "Missed", value: formatCount(facts.missed) },
        { label: "Miss rate", value: formatFraction(facts.miss_rate) },
      ];
    case "redundancy":
      return [
        { label: "Sensors in range", value: formatCount(facts.in_range) },
        { label: "Sole-served", value: formatCount(facts.sole_served) },
        { label: "Over-covered", value: formatCount(facts.over_covered) },
      ];
    case "placement":
      return [
        {
          label: "Signal (serving)",
          value: formatMeasure(facts.avg_rssi, "dBm", 0),
        },
        {
          label: "Signal (all in range)",
          value: formatMeasure(facts.avg_rssi_in_range, "dBm", 0),
        },
        { label: "Time on air", value: formatMeasure(facts.tx_mean, "s") },
      ];
    case "schedule":
      return [
        { label: "Collisions", value: formatCount(facts.collisions) },
        { label: "Readings", value: formatCount(facts.readings) },
      ];
    default:
      return [];
  }
}
